// hooks/useRecordListSort.ts
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GridSortModel } from "@mui/x-data-grid";
import { RecordManagement } from "app/class/record-management";
import { useAuth } from "app/providers/AuthProvider";
import queryKeys from "shared/services/queryKeys";

export interface SortPreference {
    field: string;
    sort: "asc" | "desc";
}

export type SortPreferences = SortPreference[];

const PREFERENCE_KEY = "record_list.sort";

interface UseRecordListSortOptions {
    controlledSortPreference?: SortPreferences | null;
    hasControlledSortPreference?: boolean;
    onSortPreferenceChange?: (pref: SortPreferences | null) => void;
    persistPreference?: boolean;
}

export const normalizeSortPreferences = (value: unknown): SortPreferences | null => {
    const preferences = Array.isArray(value) ? value : value ? [value] : [];
    const seen = new Set<string>();

    const normalized = preferences.reduce<SortPreferences>((accumulator, item) => {
        if (!item || typeof item !== "object") {
            return accumulator;
        }

        const field = String((item as SortPreference).field ?? "").trim();
        const sort = (item as SortPreference).sort === "desc" ? "desc" : "asc";

        if (!field || seen.has(field)) {
            return accumulator;
        }

        seen.add(field);
        accumulator.push({ field, sort });
        return accumulator;
    }, []);

    return normalized.length > 0 ? normalized : null;
};

export function useRecordListSort(
    tableName?: string,
    {
        controlledSortPreference,
        hasControlledSortPreference = false,
        onSortPreferenceChange,
        persistPreference = true,
    }: UseRecordListSortOptions = {}
) {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const queryKey = queryKeys.preferences.byUser(user?.id);

    const { data: preferenceRecords = [], isLoading: preferencesLoading } = useQuery({
        queryKey,
        queryFn: async () => {
            const result = await new RecordManagement("sys_user_preference")
                .where("user_id", user?.id)
                .paginate(1, 5000)
                .run();
            return result.data;
        },
        enabled: Boolean(tableName && user?.id && !hasControlledSortPreference),
    });
    const storedPreferenceValue = preferenceRecords.find(
        (record) => record.table_name === tableName && record.preference_key === PREFERENCE_KEY
    )?.preference_value;
    const sortPreference = typeof storedPreferenceValue === "string"
        ? JSON.parse(storedPreferenceValue)
        : storedPreferenceValue;
    const sortLoading = !hasControlledSortPreference && preferencesLoading;

    const { mutate } = useMutation({
        mutationFn: async (pref: SortPreferences | null) => {
            if (!persistPreference) {
                return;
            }

            if (!tableName || !user?.id) return;

            const response = await new RecordManagement("sys_user_preference").where("table_name", tableName).where("user_id", user?.id).where("preference_key", PREFERENCE_KEY).first();


            if (!pref) {
                if (!response || response.id === undefined) {
                    return;
                }

                await new RecordManagement("sys_user_preference").update(response.id, {
                    preference_value: JSON.stringify(null),
                });
                return;
            }

            if (!response || response.id === undefined) {
                await new RecordManagement("sys_user_preference").save({
                    user_id: user?.id,
                    table_name: tableName,
                    preference_key: PREFERENCE_KEY,
                    preference_value: JSON.stringify(pref),
                });
                return;
            }
            await new RecordManagement("sys_user_preference").update(response.id, {
                preference_value: JSON.stringify(pref),
            });
        },
        onSuccess: (_, pref) => {
            if (persistPreference) {
                queryClient.invalidateQueries({ queryKey });
            }
        },
    });

    const controlledNormalizedSortPreference = useMemo(
        () => normalizeSortPreferences(controlledSortPreference),
        [controlledSortPreference]
    );
    const storedNormalizedSortPreference = useMemo(
        () => normalizeSortPreferences(sortPreference),
        [sortPreference]
    );

    const effectiveSortPreference = hasControlledSortPreference
        ? controlledNormalizedSortPreference
        : storedNormalizedSortPreference;
    const effectiveSortLoading = hasControlledSortPreference ? false : sortLoading;

    const sortModel = useMemo<GridSortModel>(() => {
        if (effectiveSortLoading || !effectiveSortPreference) return [];
        const [primarySort] = effectiveSortPreference;

        if (!primarySort) {
            return [];
        }

        // The DataGrid only keeps one controlled sort reliably here, while the
        // full multi-sort state remains in our URL/preferences and query layer.
        return [{ field: primarySort.field, sort: primarySort.sort ?? "asc" }];
    }, [effectiveSortLoading, effectiveSortPreference]);

    const applySortPreference = useCallback((pref: SortPreferences | null) => {
        const nextSort = normalizeSortPreferences(pref);
        if (JSON.stringify(effectiveSortPreference) === JSON.stringify(nextSort)) {
            return;
        }

        mutate(nextSort);
        onSortPreferenceChange?.(nextSort);
    }, [effectiveSortPreference, mutate, onSortPreferenceChange]);

    const handleSortModelChange = useCallback((model: GridSortModel, gridFields?: string[]) => {
        const nextSort = normalizeSortPreferences(
            model
                .filter(({ field, sort }) => Boolean(field) && Boolean(sort))
                .map(({ field, sort }) => ({ field, sort: sort === "desc" ? "desc" : "asc" }))
        );

        if (!nextSort) {
            if (gridFields && effectiveSortPreference?.[0] && !gridFields.includes(effectiveSortPreference[0].field)) {
                return;
            }
            if (!effectiveSortPreference) {
                onSortPreferenceChange?.(null);
                return;
            }
            applySortPreference(null);
            return;
        }

        applySortPreference(nextSort);
    }, [applySortPreference, effectiveSortPreference, onSortPreferenceChange]);

    return {
        sortPreference: effectiveSortPreference,
        sortModel,
        sortLoading: effectiveSortLoading,
        handleSortModelChange,
        applySortPreference,
    };
}
